import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, BarChart3, TrendingUp, MapPin, DollarSign } from 'lucide-react';

export default function OrganizerReport() {
  const { exhibitionId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReport();
  }, [exhibitionId]); // eslint-disable-line

  const fetchReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/exhibitions/${exhibitionId}/report`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(res.data);
    } catch {
      toast.error('Rapor yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500">Rapor yükleniyor...</div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="p-8">
        <p className="text-red-500">Rapor yüklenemedi.</p>
        <Button variant="outline" onClick={() => navigate('/organizer')} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Geri
        </Button>
      </div>
    );
  }

  const { summary, area, revenue, potential_revenue, hall_stats } = report;

  const statCards = [
    { label: 'Toplam Stand', value: summary.total_stands, color: 'bg-slate-100 text-slate-700', icon: MapPin },
    { label: 'Müsait', value: summary.available, color: 'bg-emerald-50 text-emerald-700', icon: MapPin },
    { label: 'Rezerve', value: summary.reserved, color: 'bg-amber-50 text-amber-700', icon: MapPin },
    { label: 'Satıldı', value: summary.sold, color: 'bg-red-50 text-red-700', icon: MapPin },
  ];

  return (
    <div className="p-8 space-y-6" data-testid="report-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/organizer')}
            className="mb-2 -ml-2 text-gray-500"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Fuarlara Dön
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-teal-600" />
            Doluluk Raporu
          </h1>
          <p className="text-gray-500 mt-1">{report.exhibition_name}</p>
        </div>
      </div>

      {/* Stand Count Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stat-cards">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Card key={card.label} className={`${card.color} border-0`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-4 h-4 opacity-60" />
                  <span className="text-sm font-medium opacity-80">{card.label}</span>
                </div>
                <p className="text-3xl font-bold">{card.value}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Occupancy Progress */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-teal-600" />
            Doluluk Oranı
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Genel Doluluk (Satıldı + Rezerve)</span>
              <span className="font-semibold">{summary.occupancy_pct}%</span>
            </div>
            <Progress value={summary.occupancy_pct} className="h-3" data-testid="occupancy-progress" />
          </div>
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-600">Satıldı</span>
              <span className="font-semibold text-red-600">{summary.sold_pct}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div className="bg-red-500 h-2 rounded-full" style={{ width: `${summary.sold_pct}%` }} />
            </div>
          </div>

          {/* Area stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t">
            <div className="text-center p-2 bg-slate-50 rounded-lg">
              <p className="text-xs text-gray-500">Toplam Alan</p>
              <p className="font-bold text-lg">{area.total_area} m²</p>
            </div>
            <div className="text-center p-2 bg-emerald-50 rounded-lg">
              <p className="text-xs text-gray-500">Müsait Alan</p>
              <p className="font-bold text-lg text-emerald-700">{area.available_area} m²</p>
            </div>
            <div className="text-center p-2 bg-amber-50 rounded-lg">
              <p className="text-xs text-gray-500">Rezerve Alan</p>
              <p className="font-bold text-lg text-amber-700">{area.reserved_area} m²</p>
            </div>
            <div className="text-center p-2 bg-red-50 rounded-lg">
              <p className="text-xs text-gray-500">Satılan Alan</p>
              <p className="font-bold text-lg text-red-700">{area.sold_area} m²</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Revenue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Gerçekleşen Gelir (Satıldı)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(revenue).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(revenue).map(([currency, data]) => (
                  <div key={currency} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg" data-testid={`revenue-${currency}`}>
                    <div>
                      <p className="text-sm text-gray-600">{currency}</p>
                      <p className="text-xs text-gray-400">{data.stand_count} stand</p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-700">
                      {data.symbol}{data.amount.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm py-4 text-center">Henüz satış yapılmadı</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-600" />
              Potansiyel Gelir (Rezerve)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(potential_revenue).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(potential_revenue).map(([currency, data]) => (
                  <div key={currency} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg" data-testid={`potential-${currency}`}>
                    <div>
                      <p className="text-sm text-gray-600">{currency}</p>
                      <p className="text-xs text-gray-400">{data.stand_count} stand</p>
                    </div>
                    <p className="text-2xl font-bold text-amber-700">
                      {data.symbol}{data.amount.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm py-4 text-center">Rezerve stand yok</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Per-Hall Breakdown */}
      {hall_stats.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Hol Bazlı Dağılım</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="hall-stats-table">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-2 font-medium">Hol</th>
                    <th className="pb-2 font-medium text-center">Toplam</th>
                    <th className="pb-2 font-medium text-center">Müsait</th>
                    <th className="pb-2 font-medium text-center">Rezerve</th>
                    <th className="pb-2 font-medium text-center">Satıldı</th>
                    <th className="pb-2 font-medium text-center">Alan (m²)</th>
                    <th className="pb-2 font-medium text-center">Doluluk</th>
                  </tr>
                </thead>
                <tbody>
                  {hall_stats.map((h) => (
                    <tr key={h.hall_id} className="border-b last:border-0">
                      <td className="py-2.5 font-medium">{h.hall_name}</td>
                      <td className="py-2.5 text-center">{h.total_stands}</td>
                      <td className="py-2.5 text-center text-emerald-600 font-medium">{h.available}</td>
                      <td className="py-2.5 text-center text-amber-600 font-medium">{h.reserved}</td>
                      <td className="py-2.5 text-center text-red-600 font-medium">{h.sold}</td>
                      <td className="py-2.5 text-center">{h.total_area}</td>
                      <td className="py-2.5 text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <div className="w-16 bg-gray-100 rounded-full h-1.5">
                            <div
                              className="bg-teal-500 h-1.5 rounded-full"
                              style={{ width: `${h.occupancy_pct}%` }}
                            />
                          </div>
                          <span className="font-medium text-xs">{h.occupancy_pct}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
